import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  useMediaQuery,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Legend,
  CartesianGrid,
  LineChart,        
  Line,            
} from "recharts";

const REACT_APP_API_URL = "https://gamecollection-app.onrender.com/api";

const CONSOLE_COLORS = {
  "Super Nintendo Entertainment System": "#AD2E2E",
  "Nintendo Entertainment System": "#C5A900",
  "Nintendo DS": "#0E6F71",
  "Nintendo Game Boy Color": "#47A2FF",
  "Nintendo Virtual Boy": "#9B1B1B",
  "Sony Playstation 3": "#003366",
  "Sony Playstation 5": "#0F4C81",
  "Microsoft Xbox 360": "#107C10",
  "Sony Playstation 4": "#1B82D1",
  "WonderSwan Color": "#7D42AD",
  "Nintendo Game Boy Advance": "#6DA0F2",
  "Sega 32X": "#3498DB",
  "Nintendo Famicom Disk System": "#FF0000",
  "Nintendo 3DS": "#00A2E8",
  "Microsoft Xbox": "#107C10",
  "NEC PC Engine": "#B22D2D",
  "Sega Saturn": "#6A5ACD",
  "Sony Playstation 2": "#000080",
  "Nintendo Game Boy": "#4DAF4A",
  "Nintendo 64": "#FFC300",
  "Microsoft Xbox One": "#107C10",
  "Atari 2600": "#DAA520",
  "Sony Playstation Vita": "#007ACC",
  "Nintendo Pokemon Mini": "#FFCC00",
  "Sega Game Gear": "#0000FF",
  "NEC PC Engine CD": "#B22222",
  "Nintendo Wii": "#A9A9A9",
  "Sega Dreamcast": "#FF8C00",
  "Nintendo GameCube": "#663399",
  "Sony Playstation": "#0000CD",
  "SNK Neo Geo Pocket Color": "#8B008B",
  "Sony PSP": "#00008B",
  "Sega CD": "#1E90FF",
  "Nintendo Wii U": "#708090",
  "Sega Master System": "#1C1C1C",
  "SNK Neo Geo CD": "#800080",
  "WonderSwan": "#7B68EE",
  "Nintendo Switch": "#E60012",
  "Sega Genesis": "#008080",
  "Nintendo Switch 2": "#CC0000",
  Favorites: "#FFD700",
};

const getConsoleColor = (name) => CONSOLE_COLORS[name] || "#8884d8";

export default function Stats() {
  const platformId = 0;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [steamGames, setSteamGames] = useState([]);
  const [steamLoading, setSteamLoading] = useState(true);
  const [steamError, setSteamError] = useState("");
  const [barChartView, setBarChartView] = useState("all"); // toggle state

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${REACT_APP_API_URL}/platforms/${platformId}/games`)
      .then((res) => setGames(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [platformId]);

  useEffect(() => {
    axios
      .get(`${REACT_APP_API_URL}/steam/games`)
      .then((res) => setSteamGames(res.data.games))
      .catch((error) => {
        setSteamError(
          error.response?.data?.detail || "Unable to load Steam library."
        );
      })
      .finally(() => setSteamLoading(false));
  }, []);

  const {
    genreData,
    yearData,
    wordData,
    platformData,
    conditionData,
    platformDataFavorites,
    platformDataComplete,
    developerData,
    gamesByMonthData,
  } = useMemo(() => {
    const genreCount = {};
    const yearCount = {};
    const wordCount = {};
    const platformCount = {};
    const conditions = {};
    const platformCountFavorites = {};
    const platformCountComplete = {};
    const developerCount = {};

    // Compute monthly counts first (same as before)
const gamesByMonthCount = {};
games.forEach((game) => {
  if (game.date_added) {
    const date = new Date(game.date_added);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    if (year >= 2025) {
      const ym = `${year}-${month.toString().padStart(2, "0")}`;
      gamesByMonthCount[ym] = (gamesByMonthCount[ym] || 0) + 1;
    }
  }
});

// Sort months ascending
const sortedMonths = Object.keys(gamesByMonthCount).sort();

// Create cumulative data array
let runningTotal = 0;
const gamesByMonthData = sortedMonths.map((month) => {
  runningTotal += gamesByMonthCount[month];
  return { date: month, count: runningTotal };
});
    const stopWords = new Set([
      "the", "and", "for", "you", "are", "with", "that", "this", "from", "your",
      "have", "not", "but", "all", "was", "when", "they", "can", "use", "our",
      "its", "get", "one", "out", "more", "about", "some", "also", "which", "like",
      "into", "than", "other", "has", "his", "her", "their", "what", "will", "how",
      "were", "then", "may", "had", "any", "he", "she", "it", "i", "we", "my", "me",
      "do", "no", "yes", "if", "at", "on", "in", "to", "of", "a", "is", "as", "be",
      "by", "or", "so",
    ]);

    games.forEach((game) => {
      if (game.genres) {
        game.genres.split(", ").forEach((g) => {
          genreCount[g] = (genreCount[g] || 0) + 1;
        });
      }
      if (game.release_year) {
        yearCount[game.release_year] = (yearCount[game.release_year] || 0) + 1;
      }
      if (game.platform_name) {
        platformCount[game.platform_name] = (platformCount[game.platform_name] || 0) + 1;
      }
      if (game.condition) {
        conditions[game.condition] = (conditions[game.condition] || 0) + 1;
      }
      if (game.platform_name && game.favorite) {
        platformCountFavorites[game.platform_name] = (platformCountFavorites[game.platform_name] || 0) + 1;
      }
      if (game.platform_name && game.completed == 'Yes') {
        platformCountComplete[game.platform_name] = (platformCountComplete[game.platform_name] || 0) + 1;
      }
      if (game.developer) {
        developerCount[game.developer] = (developerCount[game.developer] || 0) + 1;
      }
      if (game.title) {
        game.title
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, "")
          .split(/\s+/)
          .forEach((word) => {
            if (!stopWords.has(word) && word.length > 2) {
              wordCount[word] = (wordCount[word] || 0) + 1;
            }
          });
      }
    });

    return {
      genreData: Object.entries(genreCount)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value),
      yearData: Object.entries(yearCount)
        .map(([year, count]) => ({ year, count }))
        .sort((a, b) => a.year - b.year),
      wordData: Object.entries(wordCount)
        .map(([text, value]) => ({ text, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 100),
      platformData: Object.entries(platformCount)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value),
      conditionData: Object.entries(conditions)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value),
      platformDataFavorites: Object.entries(platformCountFavorites)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value),
      platformDataComplete: Object.entries(platformCountComplete)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value),
      developerData: Object.entries(developerCount)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value),
        gamesByMonthData,
    };
  }, [games]);

  const groupedPlatformData = groupSmallSlices(platformData);
  const groupedGenreData = groupSmallSlices(genreData, 5);
  const groupedDeveloperData = groupPopularSlices(developerData, 1.3);
  const groupedConditionData = groupSmallSlices(conditionData, 1); 
  

  const fontSizeMapper = (word) => Math.log2(word.value + 1) * 8;
  const rotate = () => 0;

  return (
    <Box position="relative" p={3} sx={{ maxWidth: "1200px", margin: "auto" }}>
      {/* Word cloud background */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          opacity: 0.1,
          pointerEvents: "none",
          zIndex: 0,
          userSelect: "none",
          backgroundColor: "#fff",
        }}
      >
    
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Stat cards */}
          <Box display="flex" gap={3} flexWrap="wrap" mb={4}>
            <Paper sx={{ flex: "1 1 25%", p: 2 }}>
              <Typography variant="subtitle1">Physical Games</Typography>
              <Typography variant="h5">{games.length}</Typography>
            </Paper>
            <Paper sx={{ flex: "1 1 25%", p: 2 }}>
              <Typography variant="subtitle1">Steam Games</Typography>
              <Typography variant="h5">
                {steamLoading ? "Loading..." : steamError ? "Unavailable" : steamGames.length}
              </Typography>
            </Paper>
            <Paper sx={{ flex: "1 1 25%", p: 2 }}>
              <Typography variant="subtitle1">All Games (combined)</Typography>
              <Typography variant="h5">
                {steamLoading
                  ? "Loading..."
                  : steamError
                  ? "Unavailable"
                  : games.length + steamGames.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Physical and Steam ownerships; cross-platform duplicates count twice
              </Typography>
            </Paper>
            <Paper sx={{ flex: "1 1 25%", p: 2 }}>
              <Typography variant="subtitle1">Consoles</Typography>
              <Typography variant="h5">{platformData.length}</Typography>
            </Paper>
            <Paper sx={{ flex: "1 1 25%", p: 2 }}>
              <Typography variant="subtitle1">Most Popular Genre</Typography>
              <Typography variant="h5">{genreData[0]?.name || "N/A"}</Typography>
            </Paper>
          </Box>
          {steamError && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Steam stats are unavailable: {steamError}
            </Alert>
          )}

          {/* Charts */}
          <Box
            display="flex"
            flexDirection={isSmallScreen ? "column" : "row"}
            gap={3}
            flexWrap="wrap"
          >
            {/* Games by Console (All) Pie */}
            <Paper
              sx={{ flex: 1, minWidth: isSmallScreen ? "100%" : 300, p: 2 }}
            >
              <Typography variant="h6" mb={2}>
                Games by Console (All)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={groupedPlatformData}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={100}
                    label={
                    renderSmallLabels}
                    
                  >
                    {groupedPlatformData.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={getConsoleColor(entry.name)}
                      />
                    ))}
                  </Pie>

                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </Paper>

            {/* Games by Genre */}
            <Paper
              sx={{ flex: 1, minWidth: isSmallScreen ? "100%" : 300, p: 2 }}
            >
              <Typography variant="h6" mb={2}>
                Games by Genre
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={groupedGenreData}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={100}
                    label={
                    ({ name, percent }) =>
                            `${name} (${(percent * 100).toFixed(0)}%)`
                    }
                  />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>

            {/* Games by Developer (new) */}
            <Paper
              sx={{ flex: 1, minWidth: isSmallScreen ? "100%" : 600, p: 2 }}
            >
              <Typography variant="h6" mb={2}>
                {"Popular Developers (<1%)"}
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={groupedDeveloperData}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={100}
                    label={
                    ({ name, percent }) =>
                            `${name}`
                    }
                  >
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>

            {/* Games by Release Year */}
            <Paper
              sx={{ flex: 1, minWidth: isSmallScreen ? "100%" : 400, p: 2 }}
            >
              <Typography variant="h6" mb={2}>
                Games by Release Year
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={yearData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>

             {/* --- NEW: Games Added by Month Line Chart --- */}
             <Paper
              sx={{ flex: 1, minWidth: isSmallScreen ? "100%" : 600, p: 2 }}
            >
              <Typography variant="h6" mb={2}>
                Games Added by Month (2025+)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={gamesByMonthData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#8884d8"
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>

            {/* Games by Condition */}
            <Paper
              sx={{ flex: 1, minWidth: isSmallScreen ? "100%" : 300, p: 2 }}
            >
              <Typography variant="h6" mb={2}>
                {"Game Condition"}
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={groupedConditionData}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={100}
                    label={
                    ({ name, percent }) =>
                            `${name}`
                    }
                  >
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>

            {/* Toggle Buttons */}
            <Box sx={{ width: "100%", mb: 2, display: "flex", gap: 2 }}>
              <button
                onClick={() => setBarChartView("all")}
                style={{
                  padding: "8px 16px",
                  backgroundColor: barChartView === "all" ? "#1976d2" : "#e0e0e0",
                  color: barChartView === "all" ? "white" : "black",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Games by Console (All)
              </button>
              <button
                onClick={() => setBarChartView("favorites")}
                style={{
                  padding: "8px 16px",
                  backgroundColor: barChartView === "favorites" ? "#1976d2" : "#e0e0e0",
                  color: barChartView === "favorites" ? "white" : "black",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Games by Console (Favorites)
              </button>
               <button
                onClick={() => setBarChartView("complete")}
                style={{
                  padding: "8px 16px",
                  backgroundColor: barChartView === "complete" ? "#1976d2" : "#e0e0e0",
                  color: barChartView === "complete" ? "white" : "black",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Games by Console (Completed)
              </button>
            </Box>
            

            {/* Conditionally render the bar charts */}
            {barChartView === "all" && (
              <Paper
                sx={{ flex: 1, minWidth: isSmallScreen ? "100%" : 600, p: 2 }}
              >
                <Typography variant="h6" mb={2}>
                  Games by Console (All)
                </Typography>
                <ResponsiveContainer width="100%" height={1500}>
                  <BarChart
                    layout="vertical"
                    data={platformData}
                    margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="name"
                      type="category"
                      tick={<CustomYAxisTick />}
                      width={120}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" isAnimationActive={false}>
                      {platformData.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={getConsoleColor(entry.name)}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            )}

            {barChartView === "favorites" && (
              <Paper
                sx={{ flex: 1, minWidth: isSmallScreen ? "100%" : 600, p: 2 }}
              >
                <Typography variant="h6" mb={2}>
                  Games by Console (Favorites)
                </Typography>
                <ResponsiveContainer width="100%" height={1000}>
                  <BarChart
                    layout="vertical"
                    data={platformDataFavorites}
                    margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="name"
                      type="category"
                      tick={<CustomYAxisTick />}
                      width={120}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" isAnimationActive={false}>
                      {platformDataFavorites.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={getConsoleColor(entry.name)}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            )}
            {barChartView === "complete" && (
              <Paper
                sx={{ flex: 1, minWidth: isSmallScreen ? "100%" : 600, p: 2 }}
              >
                <Typography variant="h6" mb={2}>
                  Games by Console (Completed)
                </Typography>
                <ResponsiveContainer width="100%" height={1000}>
                  <BarChart
                    layout="vertical"
                    data={platformDataComplete}
                    margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="name"
                      type="category"
                      tick={<CustomYAxisTick />}
                      width={120}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" isAnimationActive={false}>
                      {platformDataComplete.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={getConsoleColor(entry.name)}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

const CustomYAxisTick = ({ x, y, payload }) => {
  const name = payload.value;
  const logo = getConsoleLogo(name);
  return (
    <g transform={`translate(${x},${y})`}>
      <image
        href={logo}
        x={-65}
        y={-24}
        width={48}
        height={48}
        preserveAspectRatio="xMidYMid meet"
      />
    </g>
  );
};

const CustomXAxisTick = ({ x, y, payload }) => {
  const name = payload.value;
  return (
    <image
      href={getConsoleLogo(name)}
      x={x - 25}
      y={y}
      width={50}
      height={50}
      preserveAspectRatio="xMidYMid meet"
    />
  );
};

function groupSmallSlices(data, thresholdPercent = 3.1) {
  const total = data.reduce((sum, entry) => sum + entry.value, 0);
  const bigSlices = [];
  let otherTotal = 0;
  data.forEach((entry) => {
    if ((entry.value / total) * 100 < thresholdPercent) {
      otherTotal += entry.value;
    } else {
      bigSlices.push(entry);
    }
  });
  if (otherTotal > 0) {
    bigSlices.push({ name: "Other", value: otherTotal });
  }
  return bigSlices;
}

function groupPopularSlices(data, thresholdPercent = 3.1) {
    const total = data.reduce((sum, entry) => sum + entry.value, 0);
    const bigSlices = [];
    let otherTotal = 0;
    data.forEach((entry) => {
      if ((entry.value / total) * 100 < thresholdPercent) {
        otherTotal += entry.value;
      } else {
        bigSlices.push(entry);
      }
    });
    return bigSlices;
  }

// Utility to get logo from local folder (adjust path as needed)
function getConsoleLogo(platformName) {
  try {
    return require(`./assets/old-console-logos/${platformName.replace(/ /g, "_")}.png`);
  } catch (e) {
    return require(`./assets/old-console-logos/default.png`);
  }
}

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { name, value } = payload[0].payload;
    return (
      <Box
        sx={{
          bgcolor: "white",
          border: "1px solid #ccc",
          p: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <img
          src={getConsoleLogo(name)}
          alt={name}
          style={{ width: 30, height: 30, objectFit: "contain" }}
        />
        <Typography variant="body2">
          {name}: {value}
        </Typography>
      </Box>
    );
  }
  return null;
};


const renderSmallLabels = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
    const RADIAN = Math.PI / 180;
    // Calculate label position
    const radius = innerRadius + (outerRadius - innerRadius) * 1.2;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
    return (
      <text
        x={x}
        y={y}
        fill="black"
        fontSize={10}    // smaller font size here
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${name} (${(percent * 100).toFixed(0)}%)`}
      </text>
    );
  };
  